<template>
  <div v-loading="loading || detailedPaymentValuesLoading">
    <div v-if="policies">
      <div class="bulk-pay-once__radio">
        <el-radio-group v-model="paymentTermModeSelected">
          <el-radio v-for="(value, name) in payOptions" v-if="value.length" :label="name" :key="name">
            {{ getLabel(name) }}
          </el-radio>
        </el-radio-group>
        <div v-if="(paymentTermModeSelected !== 'nextTerm') && (nextTermsDueOverdueTotalValue > 0) " class="u-warning u-mt2">
          {{ t('payments.pay_pending_next_terms_warning', {amount: asCurrency(nextTermsDueOverdueTotalValue)}) }}
        </div>
      </div>

      <div class="bulk-pay-once__info">
        <el-radio-group v-model="selectedPolicyToPay" style="display: block">
          <div v-for="el in pendingPolicies" :key="el.policyId" >
            <el-radio  :label="el.policyId" style="display: block">
              <div  class="bulk-pay-once__list">
                <div class="u-flex-horizontal-center">
                  <div class="bulk-pay-once__property">
                    <div>{{ el.address }}</div>
                    <div class="bulk-pay-once__policy u-mt1">
                     {{ t('payments.policy_#', {number: el.policyNumber}) }}
                    </div>
                  </div>
                </div>
                <div :class="el.cssClass" class="bulk-pay-once__property">
                   {{ el.dollarAmount }}
                </div>
              </div>
            </el-radio>
          </div>
        </el-radio-group>
      </div>
    </div>

    <div v-if="isFeePreviewNewYorkStyle">
      <div v-for="message in newYorkStyleFeePreviewMessages" :key="message">
        <span v-html="message" class="u-warning u-mt2 u-block"></span>
      </div>
    </div>

    <div v-if="!ownedPaymentMethods.length" class="u-mt2 u-mb2">
      <div class="u-warning">
        <i class="material-icons md-text" aria-hidden="true">warning</i>
        {{ t('payments.you_currently_have_no_payment_methods')}}
      </div>
    </div>

    <div v-if="ownedPaymentMethods.length" class="u-mt3 u-mb4">

      <div>
        <span class="subheader u-text-uppercase u-block">{{ t('payments.payment_method') }}*</span>
        <el-select v-model="selectedPaymentMethod" class="selected-payment-method">
          <el-option v-for="m in ownedPaymentMethods" :value="m.id" :key="m.id" :label="m.label"></el-option>
        </el-select>
      </div>
      <div v-if="isFeePreviewNewYorkStyle && selectedPaymentMethodObj">
        <span v-html="t('payments.payment_method_type_is_selected', {payment_method_type: selectedPaymentMethodObj.type})" 
              class="u-warning u-mt2 u-block"></span>
      </div>
      <div v-else>
        <span v-if="feeWarning" v-html="feeWarning" class="u-warning u-mt2 u-block"></span>
      </div>
    </div>

    <el-card>
      <a @click="addPaymentMethod">
        <i class="material-icons u-mr1 md-text">add</i>{{ t('payments.add_payment_method') }}
      </a>
    </el-card>

    <el-card v-if="detailedPaymentValues || detailedPaymentValuesLoading" v-loading="detailedPaymentValuesLoading">
      <div class="subheader u-text-uppercase u-mb2-5">Detailed payment amount</div>
        <div v-if="detailedPaymentValues">
          <div class="u-max-width u-flex">
            <div class="u-pr2">
              <div>Policy Amount:</div>
              <div>Convenience Fee:</div>
              <div><b>Total Amount:</b></div>
            </div>
            <div>
              <div>{{ asCurrency(detailedPaymentValues.originalAmount) }}</div>
              <div>{{ asCurrency(detailedPaymentValues.feeAmount) }}</div>
              <div><b>{{ asCurrency(detailedPaymentValues.totalAmount) }}</b></div>
            </div>
          </div>
        </div>
    </el-card>

    <div v-if="detailedPaymentValues && detailedPaymentValues.disclosureMessage">
      <div v-html="detailedPaymentValues.disclosureMessage"></div>
    </div>

    <div class="u-text-right">
      <el-button  class="el-button--primary u-text-uppercase"
                  @click="makePayment"
                  :disabled="!isCorrectPayment || this.selectedPaymentMethodId === null">
        {{ payButtonText }}
      </el-button>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'general-bulk-pay',
  props: {
    policies: {
      type: Array,
      required: true,
    },
    payOptions: {
      type: Object,
      required: true,
    },
    activePayType: {
      type: String,
      required: false,
    }
  },
  watch: {
    pendingPolicies: {
      handler (val) {
        if (!this.pendingPolicies || !this.pendingPolicies.length) {
          this.selectedPolicyToPay = null
          return
        }
        // try preselect previously selected policy
        let previouslySelectedPolicyIsInNewPolicySet = this.pendingPolicies.find(
          policy => policy.policyId === this.selectedPolicyToPay)
        if (!previouslySelectedPolicyIsInNewPolicySet) {
          this.selectedPolicyToPay = this.pendingPolicies[0].policyId
        }
      },
      immediate: true
    },
    activePayType: {
      handler (val) {
        this.paymentTermModeSelected = val
      },
      immediate: true
    },
    watchForInvalidateDetailedPaymentValues: {
      handler () {
        if (!this.selectedPolicyToPay) return  // don't run without selected policy
        this.invalidateDetailedPaymentValues()
        this.invalidateNewYorkStyleFeePreviewMessages()
      },
      immediate: true
    },
  },
  data () {
    return {
      loading: false,
      selectedPaymentMethodId: null,
      paymentTermModeSelected: '',
      detailedPaymentValues: null,
      detailedPaymentValuesLoading: false,
      selectedPolicyToPay: null,
      newYorkStyleFeePreviewMessages: [],
    }
  },
  computed: {
    ...mapGetters('payments', ['ownedPaymentMethods', 'isFeePreviewNewYorkStyle']),
    ...mapGetters('policies', ['policyById', 'policiesListByType']),
    ...mapGetters('payments', ['extendedPolicyPaymentState']),
    watchForInvalidateDetailedPaymentValues () {
      // Vue 2 doesn't support array-watching like Vue 3. So we need to return an array of values to watch.
      return [this.selectedPolicyToPay, this.selectedPaymentMethodObj, this.getCurrentNoFeeAmount,
        this.isFeePreviewNewYorkStyle]
    },
    feeWarning () {
      if (!this.selectedPaymentMethodObj) return
      if (this.selectedPaymentMethodObj.fee_data.legacy_pm) {
        // this is legacy PM
        if (this.selectedPaymentMethodObj.method === 'ACH') {
          // ACH PMs don't have fee message
          return null
        }
        // we have fee message if setting is set
        return this.setting('payments.show_cc_processing_fee_warning')
          ? this.t('payments.cc_processing_fee_warning') : null
      }
      // here only non-legacy (stripe) PMs
      if (this.detailedPaymentValues) {
        return this.detailedPaymentValues.feeMessage
      }
      // no this.detailedPaymentValues means no fee message
    },
    selectedPolicyObjToPay () {
      return this.policies.find(policy => policy.id === this.selectedPolicyToPay)
    },
    calculateAmount () {
      if (!this.selectedPolicyObjToPay) {
        return {
          currentDue: 0,
          entireTerm: 0,
          nextTerm: 0
        }
      }
      return {
        currentDue: this.selectedPolicyObjToPay.current_due,
        entireTerm: this.selectedPolicyObjToPay.payoff_amount,
        nextTerm: this.selectedPolicyObjToPay.next_due
      }
    },
    getCurrentNoFeeAmount () {
      return this.calculateAmount[this.paymentTermModeSelected]
    },
    isCorrectPayment () {
      return this.getCurrentNoFeeAmount > 0
    },
    totalAmountToPayAfterAllCalculations () {
      // just "union" of this.detailedPaymentValues.totalAmount and this.getCurrentNoFeeAmount
      return this.detailedPaymentValues ? this.detailedPaymentValues.totalAmount : this.getCurrentNoFeeAmount
    },
    payButtonText () {
      if (!this.isCorrectPayment) {
        return this.t('payments.nothing_to_pay_at_the_time')
      }
      return this.t('payments.pay_amount_now', {amount: this.asCurrency(this.totalAmountToPayAfterAllCalculations)})
    },
    selectedPaymentMethodObj () {
      if (!this.selectedPaymentMethodId) return
      return this.ownedPaymentMethods.find(x => x.id === this.selectedPaymentMethodId)
    },
    selectedPaymentMethod: {
      get () {
        if (this.selectedPaymentMethodId) {
          return this.selectedPaymentMethodId
        }
        if (this.ownedPaymentMethods && this.ownedPaymentMethods.length) {
          this.selectedPaymentMethodId = this.ownedPaymentMethods[0].id
        }
        return this.selectedPaymentMethodId
      },
      set (newValue) {
        this.selectedPaymentMethodId = newValue
      }
    },
    pendingPolicies () {
      return this.payOptions[this.paymentTermModeSelected]
    },
    activePolicies () {
      if (!this.policiesListByType) return []
      return this.policiesListByType.active
    },
    nextTermsDueOverdueTotalValue () {
      // it will return due/overdue value. Required if you see current due tab
      let policies = this.activePolicies
      let nextTermsDueOverDueTotal = 0

      for (let index in policies) {
        let policy = policies[index]
        let payState = this.extendedPolicyPaymentState(policy.id)

        if (!payState) return null

        if (payState.nextDueValue > 0 && (payState.nextDue || payState.nextOverDue)) {
          nextTermsDueOverDueTotal += payState.nextDueValue
        }
      }

      return nextTermsDueOverDueTotal
    },

  },
  methods: {
    ...mapActions('payments', ['retrieveConvenienceFee', 'retrieveConvenienceFeePreview']),

    async invalidateNewYorkStyleFeePreviewMessages () {
      if (!this.isFeePreviewNewYorkStyle) return

      if (this.getCurrentNoFeeAmount === 0) {
        this.newYorkStyleFeePreviewMessages = []
        return
      }

      try {
        let feePreviewResponse = await this.retrieveConvenienceFeePreview({
          paymentAmount: this.getCurrentNoFeeAmount,
          policyId: this.selectedPolicyToPay,
        })
        this.newYorkStyleFeePreviewMessages = feePreviewResponse.data.messages
      } catch (error) {
        this.$elAlert(error, this.t('global.error'))
        this.newYorkStyleFeePreviewMessages = []
      }
    },
    async invalidateDetailedPaymentValues () {
      if ((!this.selectedPaymentMethodObj) ||
          this.selectedPaymentMethodObj.fee_data.legacy_pm ||
          (!this.selectedPaymentMethodObj.fee_data.should_ask_fee_amount)) {
        // don't request fee data  for PMs that don't have fee (non-fee Stripe PMs or legacy PMs)
        this.detailedPaymentValues = null
        this.detailedPaymentValuesLoading = false
        return
      }
      this.detailedPaymentValuesLoading = true
      let paymentAmount = this.getCurrentNoFeeAmount

      let feeResponse = await this.retrieveConvenienceFee({
        paymentAmount,
        paymentMethod: this.selectedPaymentMethodObj.fee_data.method_type,
        policyId: this.selectedPolicyToPay,
      })

      let feeAmount = feeResponse.data.fee_amount
      this.detailedPaymentValues = {
        originalAmount: paymentAmount,
        feeAmount: feeAmount,
        feeMessage: feeResponse.data.fee_message,
        disclosureMessage: feeResponse.data.disclosure,
        totalAmount: feeAmount + paymentAmount
      }
      this.detailedPaymentValuesLoading = false
    },
    makeLogAction (paymentType, dollarAmt) {
      if (this.pendingPolicies.length > 1) {
        this.logInsuredAction('Clicked "Bulk payment"', 'custom_bulk_payment', {
          payment_type: paymentType,
          dollar_amount: dollarAmt,
        })
      }
    },
    addPaymentMethod () {
      this.$emit('handleDialogs', false)
    },
    toggleMakePaymentsDialog (val) {
      this.$emit('handleMakePaymentsDialog', val)
    },
    getLabel (name) {
      const getAmount = (name) => this.calculateAmount[name].toFixed(2)
      if (name === 'currentDue') {
        return this.t('payments.pay_current_due', {amount: getAmount(name)})
      }
      if (name === 'entireTerm') {
        return this.t('payments.pay_entire_term', {amount: getAmount(name)})
      }
      if (name === 'nextTerm') {
        return this.t('payments.pay_next_term', {amount: getAmount(name)})
      }
    },
    dollarAmount (amount) {
      return '$' + Number(amount).toFixed(2)
    },
    getAllPayment () {
      return this.calculateAmount[this.paymentTermModeSelected]
    },
    makePayment () {
      let paymentType = this.pendingPolicies[0].paymentType
      let dollarAmt = this.dollarAmount(this.totalAmountToPayAfterAllCalculations)
      let message = this.t('payments.one_time_bulk_payment_confirmation_message', {
        amount: dollarAmt,
        payment_method: this.selectedPaymentMethodObj.label,
        policies_num: 1,
        transactions_num: 1
      })
      let title = this.t('payments.one_time_confirm_payment_title')
      this.$confirm(message, title,
        {
          confirmButtonText: this.t('payments.accept'),
          cancelButtonText: this.t('payments.decline')
        }).then(() => {
          this.$emit('resetCheckedPoliciesList')  // reset make payment page's checkboxes on any payment attempt
          this.makeLogAction(paymentType, dollarAmt)
          this.loading = true
          this.$store.dispatch('payments/makePayment', {
            policy_id: this.selectedPolicyObjToPay.id,
            // payment_amount field passed to BriteCore. It SHOULD HAVE NO FEE!!! BriteCore will add/charge fee automatically
            payment_amount: this.getCurrentNoFeeAmount,
            payment_method_id: this.selectedPaymentMethodId,
            number_of_policies: 1,
            dollar_amount: dollarAmt,  // this field should be with fee to reflect real value we expect insured to pay
            policy_number: this.selectedPolicyObjToPay.policy_number,
          })
          .then(() => {
            this.loading = false
            this.toggleMakePaymentsDialog(false)
            this.paymentDidSucceed()
          })
          .catch((e) => {
            this.loading = false
            this.toggleMakePaymentsDialog(false)
            this.$elAlert(this.t('payments.one_time_error_payment_error_message'), this.t('global.error'))
          })
        })
    },
    paymentDidSucceed () {
      this.$emit('reloadPolicies')
      let title = this.t('payments.one_time_success_payment_title')
      let message = this.t('payments.one_time_success_payment_success_receipt', {
        amount: this.dollarAmount(this.getAllPayment())
      })
      this.$elAlert(title, message)
    },
  },
}
</script>

<style >
  .bulk-pay-once__info .el-radio__label {
    display: inline-block;
    width: 95%;
  }
</style>

<style scoped lang="scss">
@import '../../../sass/_variables.scss';

.bulk-pay-once {
  margin-bottom: 20px;
  border-bottom: 2px solid #EFEFEF;

  &__radio {
    padding-bottom: 20px;
    margin-bottom: 20px;
    border-bottom: 2px solid #EFEFEF;
    width: 100%;

    .el-radio__label {
      font-size: 16px;
      color: $color-black;
    }
  }
  &__info {
    margin-bottom: 20px;
    border-bottom: 2px solid #EFEFEF;
  }
  &__list {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  &__checkbox {
    width: 20px;
  }
  &__property {
    font-size: 16px;
    font-weight: 500;
    color: $color-black;
  }
  &__policy {
    font-size: 15px;
    color: #626262;
    font-weight: 300;
  }
}

.selected-payment-method {
  z-index: 0 !important;
}
</style>
